import { PayloadAction } from '@reduxjs/toolkit';
import { IRestApiResponse } from 'interfaces';
import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import { CoreSliceActions } from 'base/core/slice';
import { viewFormsActions } from 'pages/forms/slice';
import API from './constants';
import {
  IChatState, ISendFormInvitation, ISendFormToChats, ISendMessageRequestDto, IUpdateGoalRequestDto,
} from './interface';
import {
  selectConversationId, selectFilters,
  selectFormShareModalOpen, selectSelectedFormShareModalConversations,
} from './selectors';
import { chatActions } from './slice';

function* getConversationsGenerator() {
  yield put(chatActions.setLoading(true));
  const filters: IChatState['filters'] = yield select(selectFilters);
  const isFormShareModalOpen: boolean = yield select(selectFormShareModalOpen);
  let options = '';
  if (filters.keyword.length > 0) {
    options = `${options}&keyword=${filters.keyword}`;
  }
  if (filters.status !== 'all') {
    options = `${options}&isActive=${filters.status === 'active' ? 'true' : 'false'}`;
  }
  if (filters.sessions.length > 0) {
    options = `${options}&sessions=${JSON.stringify(filters.sessions)}`;
  }
  if (filters.sessionType !== 'all') {
    options = `${options}&sessionType=${filters.sessionType}`;
  }
  if (filters.chatType !== 'all') {
    options = `${options}&chatType=${filters.chatType}`;
  }
  if (isFormShareModalOpen) {
    options = `${options}&isFormPopup=true`;
  }
  if (filters.page >= 1 && isFormShareModalOpen) {
    options = `${options}&page=${filters.page + 1}`;
  }

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_GET_CONVERSATIONS.path}?isExpert=true${options}`,
        method: API.GET_GET_CONVERSATIONS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      if (isFormShareModalOpen) {
        yield put(chatActions.getFormShareModalConversationsSucceeded(response.data));
      } else {
        yield put(chatActions.getConversationsSucceeded(response.data));
      }
    } else {
      yield put(chatActions.getConversationsFailed('Conversations fetching failed'));
    }
  } catch (error) {
    yield put(chatActions.getConversationsFailed('Conversations fetching failed'));
  }
}

function* getSingleConversationGenerator() {
  const coversationId: string = yield select(selectConversationId);
  if (!coversationId) {
    yield put(chatActions.getSingleConversationFailed('empty conversation id'));
  } else {
    try {
      const response: IRestApiResponse = yield call(
        request,
        {
          path: `${API.GET_GET_SINGLE_CONVERSATION.path}/${coversationId}?isExpert=true`,
          method: API.GET_GET_SINGLE_CONVERSATION.method,
        },
        null,
        true,
      );
      if (response.statusCode === 200) {
        yield put(chatActions.getSingleConversationSucceeded(response.data));
      } else {
        yield put(chatActions.getSingleConversationFailed('Single conversation fetching failed'));
      }
    } catch (error) {
      yield put(chatActions.getSingleConversationFailed('Single conversation fetching failed'));
    }
  }
}

function* sendMessageGenerator(action: PayloadAction<ISendMessageRequestDto>) {
  const coversationId: string = yield select(selectConversationId);

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.PUT_SEND_MESSAGE.path}/${coversationId}?isExpert=true`,
        method: API.PUT_SEND_MESSAGE.method,
      },
      action.payload,
      true,
    );
    if (response.statusCode === 200) {
      yield put(chatActions.sendMessageSucceeded());
      yield put(chatActions.getConversations());
      yield action.payload.resolve();
    } else {
      yield put(chatActions.sendMessageFailed('Single conversation fetching failed'));
      yield action.payload.reject();
    }
  } catch (error) {
    yield put(chatActions.sendMessageFailed('Single conversation fetching failed'));
    yield action.payload.reject();
  }
}

function* sendMeetingMessageGenerator(action: PayloadAction<{ resolve: any; reject: any; }>) {
  const coversationId: string = yield select(selectConversationId);

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_SEND_MEETING_MESSAGE.path}/${coversationId}?isExpert=true`,
        method: API.POST_SEND_MEETING_MESSAGE.method,
      },
      {
        body: '',
      },
      true,
    );
    if (response.statusCode === 200) {
      yield put(chatActions.sendMeetingMessageSucceeded());
      yield action.payload.resolve();
    } else {
      yield put(chatActions.sendMeetingMessageFailed('Failed to create instant meeting'));
      yield action.payload.reject();
    }
  } catch (error) {
    yield put(chatActions.sendMeetingMessageFailed('Failed to create instant meeting'));
    yield action.payload.reject();
  }
}

function* completeSessionGenerator(action: PayloadAction<string>) {
  const userSessionId: string = yield action.payload;

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.PUT_CLOSE_SESSION.path}/${userSessionId}`,
        method: API.PUT_CLOSE_SESSION.method,
      },
      { userSessionId },
      true,
    );
    if (response.statusCode === 200) {
      yield put(chatActions.completeSessionSucceeded());
    } else {
      yield put(chatActions.completeSessionFailed('Single conversation fetching failed'));
    }
  } catch (error) {
    yield put(chatActions.completeSessionFailed('Single conversation fetching failed'));
  }
}

function* updateGoalsGenerator(action: PayloadAction<IUpdateGoalRequestDto>) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.POST_GOAL_UPDATE.path}`,
        method: API.POST_GOAL_UPDATE.method,
      },
      action.payload,
      true,
    );
    if (response.statusCode === 200) {
      yield put(chatActions.updateGoalsSucceeded());
      yield put(chatActions.getSingleConversation());
    } else {
      yield put(chatActions.updateGoalsFailed('Goal update failed'));
    }
  } catch (error) {
    yield put(chatActions.updateGoalsFailed('Goal update failed'));
  }
}

function* readAllConversationMessagesGenerator() {
  const coversationId: string = yield select(selectConversationId);

  if (!coversationId) {
    return;
  }

  try {
    yield call(
      request,
      {
        path: `${API.POST_READ_ALL_CHAT_CONVERSATIONS.path.replace(':chatId', coversationId)}?isExpert=true`,
        method: API.POST_READ_ALL_CHAT_CONVERSATIONS.method,
      },
      {},
      true,
    );
  } catch (err) {
    console.error(err);
  }
}

function* sendFormToChatsGenerator(action: PayloadAction<ISendFormToChats>) {
  const selectedConversations: any[] = yield select(selectSelectedFormShareModalConversations);

  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: API.POST_SEND_FORM_TO_CHATS.path,
        method: API.POST_SEND_FORM_TO_CHATS.method,
      },
      {
        ...action.payload,
        chatData: selectedConversations.map((c) => ({
          chatId: c.id,
          userSessionId: c.userSessionID,
          ...(c.liveClassId && { liveClassId: c.liveClassId }),
        })),
      },
      true,
    );
    if (response.statusCode === 201) {
      yield put(CoreSliceActions.setMessage(
        {
          message: response.data.message || 'Form sent successfully!',
          success: true,
        },
      ));
      yield put(chatActions.sendFormToChatsSucceeded());
    } else {
      yield put(CoreSliceActions.setMessage(
        {
          message: 'Failed to send the form!',
          success: false,
        },
      ));
      yield put(chatActions.sendFormToChatsFailed());
    }
  } catch (error) {
    yield put(CoreSliceActions.setMessage(
      {
        message: 'Failed to send the form!',
        success: false,
      },
    ));
    yield put(chatActions.sendFormToChatsFailed());
  }
}

function* sendFormInviteGenerator(action: PayloadAction<ISendFormInvitation>) {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: API.POST_SEND_FORM_INVITE.path,
        method: API.POST_SEND_FORM_INVITE.method,
      },
      action.payload,
      true,
    );
    if (response.statusCode === 201) {
      yield put(chatActions.getConversations());
      yield put(chatActions.sendFormInviteSucceeded({
        isSendByTheFormListPage: action.payload.isSendByTheFormListPage,
      }));
      yield put(viewFormsActions.clearFilteredForms());

      yield put(chatActions.setFormShareThoughChatModalSuccess(true));
      yield put(
        CoreSliceActions.setMessage({
          message: 'Form invite sent successfully!',
          success: true,
        }),
      );
    } else {
      yield put(chatActions.sendFormInviteFailed());
      yield put(
        CoreSliceActions.setMessage({
          message: 'Failed to send the form invite!',
          success: false,
        }),
      );
    }
  } catch (error) {
    yield put(chatActions.sendFormInviteFailed());
    yield put(
      CoreSliceActions.setMessage({
        message: 'Failed to send the form invite!',
        success: false,
      }),
    );
  }
}

export function* chatSaga() {
  yield all([
    takeLatest(chatActions.getConversations.type, getConversationsGenerator),
    takeLatest(chatActions.getSingleConversation.type, getSingleConversationGenerator),
    takeLatest(chatActions.setConversationId.type, getSingleConversationGenerator),
    takeLatest(chatActions.setKeyword.type, getConversationsGenerator),
    takeLatest(chatActions.setStatus.type, getConversationsGenerator),
    takeLatest(chatActions.setSessions.type, getConversationsGenerator),
    takeLatest(chatActions.setSessionType.type, getConversationsGenerator),
    takeLatest(chatActions.setChatType.type, getConversationsGenerator),
    takeLatest(chatActions.setPage.type, getConversationsGenerator),
    takeLatest(chatActions.sendMessage.type, sendMessageGenerator),
    takeLatest(chatActions.sendMeetingMessage.type, sendMeetingMessageGenerator),
    takeLatest(chatActions.completeSession.type, completeSessionGenerator),
    takeLatest(chatActions.updateGoals.type, updateGoalsGenerator),
    takeLatest(chatActions.readAllConversationMessages.type, readAllConversationMessagesGenerator),
    takeLatest(chatActions.sendFormToChats.type, sendFormToChatsGenerator),
    takeLatest(chatActions.sendFormInvite.type, sendFormInviteGenerator),
  ]);
}

export default chatSaga;
